/**
 * Form 4180 (Excise-Tax workflow, 03-17) child line-item API. The four interview
 * lists are nested under the 4180 `FormSubmission` at
 * `/forms/{submissionId}/{individuals,banks,eftps,positions}/`. Follows the
 * settled envelope convention: `apiFetch` returns `{ data }`; we unwrap it and
 * map snake↔camel per list. The flat interview fields live in the submission's
 * `data` blob and go through the shared forms api — only these lists are here.
 */

import { apiFetch } from "@/lib/api";

interface Envelope<T> {
  data: T;
}

export interface IndividualItem {
  id: string;
  name: string;
  phone: string;
  dateFrom: string;
  dateTo: string;
}

export interface BankItem {
  id: string;
  bankName: string;
  /** Masked (`••••1234`) in list responses; full on a single retrieve. PII. */
  accountNumber: string;
  dateFrom: string;
  dateTo: string;
}

export interface EftpsItem {
  id: string;
  whereEftps: string;
  whomEftps: string;
}

export interface PositionItem {
  id: string;
  position: string;
  name: string;
  address: string;
  dateFrom: string;
  dateTo: string;
}

const nn = (v: unknown): string => (v == null ? "" : String(v));

function toIndividual(r: Record<string, unknown>): IndividualItem {
  return { id: String(r.id), name: nn(r.name), phone: nn(r.phone), dateFrom: nn(r.date_from), dateTo: nn(r.date_to) };
}
function toBank(r: Record<string, unknown>): BankItem {
  return {
    id: String(r.id),
    bankName: nn(r.bank_name),
    accountNumber: nn(r.account_number),
    dateFrom: nn(r.date_from),
    dateTo: nn(r.date_to),
  };
}
function toEftps(r: Record<string, unknown>): EftpsItem {
  return { id: String(r.id), whereEftps: nn(r.where_eftps), whomEftps: nn(r.whom_eftps) };
}
function toPosition(r: Record<string, unknown>): PositionItem {
  return {
    id: String(r.id),
    position: nn(r.position),
    name: nn(r.name),
    address: nn(r.address),
    dateFrom: nn(r.date_from),
    dateTo: nn(r.date_to),
  };
}

/** Build a firm-scoped CRUD client for one 4180 child list. */
function childApi<T>(segment: string, map: (r: Record<string, unknown>) => T) {
  const base = (subId: string) => `/forms/${subId}/${segment}/`;
  const detail = (subId: string, id: string) => `/forms/${subId}/${segment}/${id}/`;
  return {
    async list(submissionId: string): Promise<T[]> {
      const res = await apiFetch<Envelope<Record<string, unknown>[]>>(base(submissionId));
      return res.data.map(map);
    },
    async get(submissionId: string, id: string): Promise<T> {
      const res = await apiFetch<Envelope<Record<string, unknown>>>(detail(submissionId, id));
      return map(res.data);
    },
    async create(submissionId: string, payload: Record<string, unknown>): Promise<T> {
      const res = await apiFetch<Envelope<Record<string, unknown>>>(base(submissionId), {
        method: "POST",
        json: payload,
      });
      return map(res.data);
    },
    async update(submissionId: string, id: string, payload: Record<string, unknown>): Promise<T> {
      const res = await apiFetch<Envelope<Record<string, unknown>>>(detail(submissionId, id), {
        method: "PATCH",
        json: payload,
      });
      return map(res.data);
    },
    remove(submissionId: string, id: string): Promise<void> {
      return apiFetch<void>(detail(submissionId, id), { method: "DELETE" });
    },
  };
}

export const individualsApi = childApi<IndividualItem>("individuals", toIndividual);
export const banksApi = childApi<BankItem>("banks", toBank);
export const eftpsApi = childApi<EftpsItem>("eftps", toEftps);
export const positionsApi = childApi<PositionItem>("positions", toPosition);
